import React, { useState } from 'react';
import { Layers, Tag, Truck, Archive, ArchiveRestore, X, Percent } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { inv } from '../../lib/invApi';

const ACTIONS = {
  add_trades: { label: 'Add Trade(s)', icon: Layers },
  remove_trades: { label: 'Remove Trade(s)', icon: Layers },
  set_category: { label: 'Set Category', icon: Tag },
  set_supplier: { label: 'Set Supplier', icon: Truck },
  archive: { label: 'Archive', icon: Archive },
  unarchive: { label: 'Reactivate', icon: ArchiveRestore },
};
const sel = 'w-full h-10 rounded-md bg-[#0A0A0A] border border-[#262626] text-white text-sm px-3';

// Safe administrative bulk changes. Never changes quantity, cost, pricing or barcodes.
export const BulkActionsBar = ({ selectedIds, trades, categories, suppliers, onClear, onDone, onBulkPricing }) => {
  const [action, setAction] = useState(null);
  const [pickTrades, setPickTrades] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [supplier, setSupplier] = useState('');
  const [busy, setBusy] = useState(false);
  const n = selectedIds.length;
  if (!n) return null;

  const open = (a) => { setAction(a); setPickTrades([]); setCategoryId(''); setSupplier(''); };
  const apply = async () => {
    const body = { item_ids: selectedIds, action };
    if (action.endsWith('_trades')) { if (!pickTrades.length) { toast.error('Pick at least one Trade'); return; } body.trades = pickTrades; }
    if (action === 'set_category') body.category_id = categoryId || null;
    if (action === 'set_supplier') body.supplier = supplier || null;
    setBusy(true);
    try {
      const { data } = await inv.post('/items/bulk-update', body);
      toast.success(`${ACTIONS[action].label}: ${data.changed} of ${data.selected} item${data.selected === 1 ? '' : 's'} changed`);
      setAction(null);
      onDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk change failed'); }
    setBusy(false);
  };
  const toggleTrade = (t) => setPickTrades((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));

  return (
    <div data-testid="inv-bulk-bar" className="flex flex-wrap items-center gap-2 rounded-md border border-[#D4AF37]/40 bg-[#1f1b10] px-3 py-2">
      <span data-testid="inv-bulk-count" className="text-sm text-[#D4AF37] font-semibold mr-1">{n} selected</span>
      {Object.entries(ACTIONS).map(([k, a]) => (
        <Button key={k} data-testid={`inv-bulk-${k}`} size="sm" variant="outline" onClick={() => open(k)} className="h-8 border-[#262626] text-white hover:bg-[#262626]">
          <a.icon className="w-3.5 h-3.5 mr-1" />{a.label}
        </Button>
      ))}
      <Button data-testid="inv-bulk-pricing" size="sm" variant="outline" onClick={onBulkPricing} className="h-8 border-[#262626] text-white hover:bg-[#262626]"><Percent className="w-3.5 h-3.5 mr-1" />Bulk Pricing</Button>
      <Button data-testid="inv-bulk-clear" size="sm" variant="ghost" onClick={onClear} className="h-8 text-[#A3A3A3] ml-auto"><X className="w-3.5 h-3.5 mr-1" />Clear</Button>

      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent data-testid="inv-bulk-dialog" className="bg-[#141414] border-[#262626] text-white max-w-md">
          <DialogHeader>
            <DialogTitle>{action && ACTIONS[action].label} — {n} item{n === 1 ? '' : 's'}</DialogTitle>
            <DialogDescription className="text-[#A3A3A3] text-xs">Each changed item is recorded in the audit history. Quantities, cost, pricing and barcodes are not touched.</DialogDescription>
          </DialogHeader>
          {action?.endsWith('_trades') && (
            <div className="flex flex-wrap gap-2" data-testid="inv-bulk-trade-picker">
              {trades.map((t) => (
                <button key={t.id} type="button" data-testid={`inv-bulk-trade-${t.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`} onClick={() => toggleTrade(t.name)}
                  className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${pickTrades.includes(t.name) ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]' : 'border-[#262626] text-[#A3A3A3] hover:text-white'}`}>{t.name}</button>
              ))}
            </div>
          )}
          {action === 'set_category' && (
            <select data-testid="inv-bulk-category-select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className={sel}>
              <option value="">No category (clear)</option>
              {categories.filter((c) => c.active !== false).map((c) => <option key={c.id} value={c.id}>{c.name}{c.department ? ` · ${c.department}` : ''}</option>)}
            </select>
          )}
          {action === 'set_supplier' && (
            <select data-testid="inv-bulk-supplier-select" value={supplier} onChange={(e) => setSupplier(e.target.value)} className={sel}>
              <option value="">No supplier (clear)</option>
              {suppliers.filter((s) => s.active !== false).map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          )}
          {(action === 'archive' || action === 'unarchive') && (
            <p className="text-sm text-[#A3A3A3]">{action === 'archive' ? 'Archived items are hidden from active inventory; all stock, barcode and movement history is kept.' : 'Reactivated items return to active inventory.'}</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="border-[#262626]" onClick={() => setAction(null)}>Cancel</Button>
            <Button data-testid="inv-bulk-apply" disabled={busy} onClick={apply} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{busy ? 'Applying…' : 'Apply'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BulkActionsBar;
